import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { Accordion, AccordionDetails, Box, Card, CircularProgress, Collapse, Grid, Stack } from '@mui/material'
import useTitle from 'utils/customHook/useTitle'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getLabel } from 'utils/StoreUtils'
import { getBookmarks } from 'utils/StationUtils'
import { getSandreLabel, getSandreMnemonique, searchAllCharacters } from 'utils/StringUtil'
import { getUserBookmarksByStationType } from 'utils/UserUtil'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { chunk, flatten, fromPairs, isNil, keyBy, some, sortBy, template } from 'lodash'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import HomeAction from 'home/actions/HomeAction'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { hasValue } from 'utils/NumberUtil'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import moment from 'moment'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { DEFAULT_SUPPORT, DEFAULT_SUPPORT_VALUE, SEUILS_LIM, SEUILS_REF } from 'quality/components/AEPoverview/AEPOverview'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import ToastrAction from 'toastr/actions/ToastrAction'
import LogAction from 'log/actions/LogAction'
import { CardTitle } from 'components/card/NewCard'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import ProgressCard from 'components/card/ProgressCard'
import { ConformityOverviewTables } from 'quality/components/AEPoverview/AEPoverviewPanel'
import { exportFile } from 'utils/ExportDataUtil'
import AccountAction from 'account/actions/AccountAction'
import DistributionUnitsConformityFilter from './DistributionUnitsConformityFilter'
import { push } from '@lagunovsky/redux-react-router'
import { CONFORMITY_OVERVIEW_EXPORT_HEADERS, getConformityFilterBody, getConformityOverviewExportRows } from 'quality/utils/conformityExportUtils'
import { getComplianceStatus, NO_RECENT_OPERATION } from 'station/constants/ComplianceConstants'
import { AccordionTitle } from 'components/styled/Accordions'
import { greyBlue } from 'utils/constants/ColorTheme'
import useActions from 'utils/customHook/useActions'
import useAbortController from 'utils/customHook/useAbortController'
import DistributionUnitAction from 'distributionUnit/actions/DistributionUnitAction'
import PropTypes from 'prop-types'

const SEARCH_VALUE_HEADERS = ['number', 'code', 'name', 'city', 'status', 'SISEeaucode', 'creationDate', 'stationTypeLabel', 'operator', 'administrator', 'referent', 'department']
const STATION_TYPE = STATION_TYPE_NAME.distributionUnit
const STORAGE_SEUILS_REF = 'DEFAULT_REFERENCE_THRESHOLD'
const STORAGE_SEUILS_LIM = 'DEFAULT_LIMIT_THRESHOLD'
const STORAGE_ONLY_WITH_DATA = 'DISTRIBUTION_UNITS_CONFORMITY_ONLY_WITH_DATA'
const STORAGE_EXPANDED = 'DISTRIBUTION_UNITS_CONFORMITY_EXPANDED'
const STORAGE_FILTERS = 'DISTRIBUTION_UNITS_CONFORMITY_FILTERS'

const EXPORT_HEADERS = ['udiCode', 'udi', ...CONFORMITY_OVERVIEW_EXPORT_HEADERS]
// nombre d'UDI par appel du bilan conformité
// paquets d'UDI calculés par le back, traités 2 par 2 : taille adaptée au nombre d'UDI (une dizaine de paquets),
// entre 5 (progression régulière sur peu d'UDI) et 20 (nombre d'appels limité sur beaucoup d'UDI)
const MIN_GROUP_SIZE = 5
const MAX_GROUP_SIZE = 20
const TARGET_GROUPS = 10
const PARALLEL_GROUPS = 2
const getGroupSize = nbUnits => Math.min(MAX_GROUP_SIZE, Math.max(MIN_GROUP_SIZE, Math.ceil(nbUnits / TARGET_GROUPS)))

// progression affichée : extrapolée entre deux réceptions de paquets à partir du temps moyen par UDI
const PROGRESS_REFRESH_MS = 200
const DEFAULT_MS_PER_UNIT = 300 // estimation tant qu'aucun paquet n'est reçu
const MAX_IN_FLIGHT_RATIO = 0.95 // l'extrapolation ne dépasse jamais les paquets en cours

const getExportData = (udi, result, filter, producers) => getConformityOverviewExportRows(result, filter, producers).map(row => ({
    udiCode: { value: udi.code },
    udi: { value: udi.name },
    ...row,
}))

const withHeaders = rows => rows.length ? [{ ...rows[0], headers: EXPORT_HEADERS }, ...rows.slice(1)] : []

const DistributionUnitConformityAccordion = ({
    udi = {},
    result,
    loading = false,
    isSearchDone = false,
    filter = {},
    expanded = false,
    onToggle = () => { },
}) => {
    const dispatch = useDispatch()
    const producers = useSelector(store => store.ContributorReducer.producers)

    // accordéon fermé tant que le bilan de l'unité n'a pas de données
    const hasData = !loading && !!result?.nbAnalysis

    // le bilan de l'unité s'ouvre avec les mêmes critères (année, support, seuils)
    const unitUrl = () => {
        const params = new URLSearchParams()
        const add = (key, value) => !isNil(value) && params.set(key, `${value}`)
        add('year', filter.year)
        params.set('support', filter.support ?? '')
        add('referenceThreshold', filter.referenceThreshold)
        add('limitThreshold', filter.limitThreshold)
        return `/station/${STATION_TYPE}/${udi.id}/conformityOverview/?${params.toString()}`
    }

    const getContent = () => {
        if (!isSearchDone) {
            return <Grid>{i18n.pleaseSearch}</Grid>
        }
        if (loading || !result) {
            return <ProgressCard indeterminate />
        }
        if (result.error) {
            return <Grid>{i18n.loadError}</Grid>
        }
        if (result.cancelled) {
            return <Grid>{i18n.searchCancelled}</Grid>
        }
        if (!result.nbAnalysis) {
            return <Grid>{template(i18n.noAnalysisForYear)({ year: filter.year })}</Grid>
        }
        return <ConformityOverviewTables result={result} filter={filter} />
    }

    // statut de conformité de l'UDI (même calcul que l'observatoire)
    // statut calculé sur l'année demandée : "aucune opération récente" signifie ici "aucune opération dans l'année"
    const complianceStatus = getComplianceStatus(result?.status)
    const status = result?.status === NO_RECENT_OPERATION
        ? { ...complianceStatus, label: template(i18n.noOperationForYear)({ year: filter.year }) }
        : complianceStatus

    return (
        <Accordion
            expanded={expanded && hasData}
            onChange={(_, value) => hasData && onToggle(udi.id, value)}
            sx={{
                marginBottom: '1rem',
                '&.Mui-expanded': { marginBottom: '1rem' },
                '&::before': { display: 'none' },
                // sans données : simple bandeau (pas de flèche ni d'ouverture au clic)
                ...(hasData ? {} : {
                    '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
                    '& .MuiAccordionSummary-root': { cursor: 'default' },
                }),
            }}
        >
            <AccordionTitle
                color={greyBlue}
                title={`${udi.code ?? ''} - ${udi.name ?? ''}`}
                subTitle={status ? (
                    <span style={{ backgroundColor: status.color, color: 'white', borderRadius: 4, padding: '0.15rem 0.75rem', fontSize: '1rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {status.label}
                    </span>
                ) : undefined}
                subtitleSize='auto'
                actions={[
                    // spinner tant que le bilan de l'unité n'est pas reçu
                    ...(loading ? [(
                        <Box key='loading' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress thickness={5} size={20} sx={{ '& .MuiCircularProgress-svg': { backgroundColor: 'inherit' }, color: 'white' }} />
                        </Box>
                    )] : []),
                    {
                        icon: 'launch',
                        tooltip: i18n.seeDistributionUnit,
                        color: 'white',
                        onClick: () => dispatch(push(unitUrl())),
                    },
                    {
                        displayed: !!result?.nbAnalysis,
                        icon: 'file_download',
                        tooltip: i18n.export,
                        color: 'white',
                        onClick: () => exportFile({
                            data: withHeaders(getExportData(udi, result, filter, producers)),
                            exportType: 'xlsx',
                            titleFile: `${i18n.AEPoverview} ${udi.code || udi.id}`,
                        }),
                    },
                ]}
            />
            <AccordionDetails>
                {expanded && hasData && getContent()}
            </AccordionDetails>
        </Accordion>
    )
}

DistributionUnitConformityAccordion.propTypes = {
    udi: PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    }),
    result: PropTypes.shape({
        error: PropTypes.bool,
        cancelled: PropTypes.bool,
        nbAnalysis: PropTypes.number,
        status: PropTypes.string,
        producers: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    loading: PropTypes.bool,
    isSearchDone: PropTypes.bool,
    filter: PropTypes.shape({
        year: PropTypes.number,
    }),
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
}

const DistributionUnitsConformity = () => {
    const {
        accountUserSettings,
        applicationSettings,
        userBookmarks,
        contributorLinks,
        contributors,
        sandreCodes,
        distributionUnits,
        distributionUnitSectors,
        selectedSearchValues,
        globalResearch,
        producers,
    } = useSelector(store => ({
        accountUserSettings: store.AccountReducer.accountUserSettings,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        userBookmarks: store.UserReducer.userBookmarks,
        contributorLinks: store.StationReducer.contributorLinks,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        distributionUnitSectors: store.DistributionUnitReducer.distributionUnitSectors,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        globalResearch: store.HomeReducer.globalResearch,
        producers: store.ContributorReducer.producers,
    }), shallowEqual)

    const dispatch = useDispatch()

    const defaultSupport = useAccountSetting(DEFAULT_SUPPORT, setting => setting || undefined)
    const thresholdRefSettings = useApplicationSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdRefAccountSettings = useAccountSetting(SEUILS_REF, setting => setting ? parseInt(setting) : undefined)
    const thresholdRefDefaultSettings = useLocalStorage(STORAGE_SEUILS_REF, setting => setting ? parseInt(setting) : undefined)[0]

    const thresholdLimSettings = useApplicationSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimAccountSettings = useAccountSetting(SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)
    const thresholdLimDefaultSettings = useLocalStorage(STORAGE_SEUILS_LIM, setting => setting ? parseInt(setting) : undefined)[0]

    const operator = useApplicationSetting('contributorTypeOperator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const administrator = useApplicationSetting('contributorTypeAdministrator', d => parseInt(d)) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    // derniers filtres validés, gardés en cache et restaurés à l'ouverture de l'écran
    const [cachedFilters, setCachedFilters, removeCachedFilters] = useLocalStorage(STORAGE_FILTERS)

    const [filter, setFilter] = useState(cachedFilters?.filter ?? selectedSearchValues[STATION_TYPE]?.filter ?? FILTER_TYPE.NOFILTER)
    const [filterResults, setFilterResults] = useState(cachedFilters?.filterResults ?? selectedSearchValues[STATION_TYPE]?.filterResults ?? [])
    const [searchValue, setSearchValue] = useState(globalResearch || (cachedFilters?.searchValue ?? selectedSearchValues[STATION_TYPE]?.searchValue ?? ''))
    const [city, setCity] = useState(cachedFilters?.city)
    const [sector, setSector] = useState(cachedFilters?.sector)
    const [onlyWithData, setOnlyWithData] = useLocalStorage(STORAGE_ONLY_WITH_DATA, false)
    const [expandedUnits, setExpandedUnits] = useLocalStorage(STORAGE_EXPANDED, {})

    const getDefaultConformityFilter = () => ({
        support: defaultSupport,
        // par défaut : année en cours
        year: moment().year(),
        producer: undefined,
        referenceThreshold: thresholdRefDefaultSettings ?? thresholdRefAccountSettings ?? thresholdRefSettings,
        limitThreshold: thresholdLimDefaultSettings ?? thresholdLimAccountSettings ?? thresholdLimSettings,
    })
    const [conformityFilter, setConformityFilter] = useState(() => cachedFilters?.conformityFilter ?? getDefaultConformityFilter())
    // incrémenté à la réinitialisation pour remonter le formulaire de filtres avec les valeurs par défaut
    const [resetKey, setResetKey] = useState(0)
    // incrémenté à chaque recherche pour relancer les calculs
    const [searchId, setSearchId] = useState(0)
    // bilan par UDI (id -> résultat du back), rempli au fil des appels séquentiels
    const [results, setResults] = useState({})

    const { controllerRef, initController } = useAbortController(true)

    const {
        progress: criteriasProgress,
        isLoaded,
    } = useProgressDispatch(() => [
        dispatch(ContributorAction.fetchProducers()),
    ])

    useEffect(() => {
        if (!accountUserSettings.length) {
            dispatch(AccountAction.fetchAccountUserSettings())
        }
        if (!applicationSettings.length) {
            dispatch(AdministrationAction.fetchSettings())
        }
    }, [])

    useEffect(() => {
        if (globalResearch) {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
    }, [])

    const getContributor = useCallback((up, ref) => {
        const contributorLink = contributorLinks.find(cl => up.id === cl.idStation && cl.contributorType === ref && !cl.endDate)
        const contributor = contributorLink && contributors.find(c => contributorLink.idContributor === c.id)
        return contributor && (contributor.mnemonique || contributor.name) || ''
    }, [contributorLinks, contributors])

    const distributionUnitsFormatted = useMemo(() => {
        if (distributionUnits.length) {
            return sortBy(distributionUnits, o => o.name ? o.name.toUpperCase() : '}')
                .map(udi => ({
                    ...udi,
                    sector: getLabel(distributionUnitSectors, udi.id, 'name', 'distributionUnitId'),
                    sectorObj: distributionUnitSectors.find(s => s.distributionUnitId === udi.id),
                    bookmark: getBookmarks(udi.code, getUserBookmarksByStationType(userBookmarks, 'distributionUnit', udi.code)),
                    waterNature: getSandreLabel(sandreCodes, SANDRE.NATURE_EAU, udi.waterNature),
                    operator: getContributor(udi, operator),
                    collectivity: getContributor(udi, administrator),
                    exploitationModeCode: getSandreMnemonique(sandreCodes, SANDRE.MODE_EXPLOITATION, udi.exploitationModeCode),
                    status: getSandreLabel(sandreCodes, SANDRE.CODE_ETAT, udi.state),
                }))
        }
        return []
    }, [distributionUnits, distributionUnitSectors, userBookmarks, sandreCodes, getContributor, operator, administrator])

    const distributionUnitsFiltered = useMemo(() => {
        const udiBySector = (sector && sector !== FILTER_TYPE.NOFILTER) ? distributionUnitsFormatted.filter(udi => udi.sectorObj?.code === sector) : distributionUnitsFormatted
        return city ? udiBySector.filter(udi => udi.geographicSector?.split(',')?.includes(city)) : udiBySector
    }, [city, distributionUnitsFormatted, sector])

    const containsSearchValue = useCallback((station) =>
        some(SEARCH_VALUE_HEADERS, prop => station[prop]
            ? searchAllCharacters(station[prop].toString()).includes(searchAllCharacters(searchValue))
            : false,
        ), [searchValue])

    const filteredUnits = useMemo(() => {
        const searchFiltered = searchValue ? distributionUnitsFiltered.filter(s => containsSearchValue(s)) : distributionUnitsFiltered
        if (!hasValue(filter) || `${filter}` === `${FILTER_TYPE.NOFILTER}`) {
            return searchFiltered
        } else if (`${filter}` === `${FILTER_TYPE.BOOKMARK}`) {
            const bookmarks = getUserBookmarksByStationType(userBookmarks, STATION_TYPE)
            return flatten(bookmarks.map(code => searchFiltered.find(s => s.code.includes(code)) || []))
        }
        if (filterResults.length) {
            return flatten(filterResults.map(fr => searchFiltered.find(station => station.id === parseInt(fr.id)) || []))
        }
        return []
    }, [searchValue, distributionUnitsFiltered, filter, filterResults, containsSearchValue, userBookmarks])

    const filteredUnitIds = useMemo(() => filteredUnits.map(({ id }) => id), [filteredUnits])

    // producteurs, supports et années proposés : uniquement ceux présents dans les opérations des UDI
    const [criterias, setCriterias] = useState({})
    const unitIdsKey = useMemo(() => distributionUnits.map(({ id }) => id).join(','), [distributionUnits])
    useEffect(() => {
        if (!unitIdsKey) {
            return undefined
        }
        const controller = new AbortController()
        DistributionUnitAction.promiseConformityCriterias(unitIdsKey.split(',').map(id => parseInt(id)), controller.signal)
            .then(setCriterias)
            .catch(() => { })
        return () => controller.abort()
    }, [unitIdsKey])

    useTitle(() => {
        return [{
            title: i18n.distributionUnit,
            href: 'distributionUnit',
        }, {
            title: i18n.AEPoverview,
            href: 'station/distributionUnit/conformityOverview',
        }]
    }, [])

    // la recherche ne repart que si la liste des UDI change réellement (le tableau est recréé à chaque rendu)
    const filteredUnitIdsKey = filteredUnitIds.join(',')

    // calcul côté back, par paquets d'UDI (PARALLEL_GROUPS à la fois) : chaque paquet s'affiche dès qu'il est reçu
    useEffect(() => {
        if (!searchId) {
            return
        }
        controllerRef.current.abort()
        initController()
        const { signal } = controllerRef.current
        const body = getConformityFilterBody(conformityFilter)
        const groups = chunk(filteredUnitIds, getGroupSize(filteredUnitIds.length))
        setResults({})

        let errorNotified = false
        let nextGroup = 0
        const computeNext = async () => {
            if (signal.aborted || nextGroup >= groups.length) {
                return
            }
            const ids = groups[nextGroup]
            nextGroup += 1
            const groupResults = await DistributionUnitAction.promiseConformity(ids, body, signal).catch(err => {
                if (!signal.aborted) {
                    dispatch(LogAction.logError(`${i18n.fetchError}${i18n.AEPoverview} : ${err}`))
                }
                return undefined
            })
            if (signal.aborted) {
                return
            }
            if (!Array.isArray(groupResults) && !errorNotified) {
                errorNotified = true
                dispatch(ToastrAction.error(`${i18n.fetchError}${i18n.AEPoverview}`))
            }
            // une UDI absente de la réponse est en erreur : elle reste affichée (même avec "uniquement les unités avec données")
            const resultsById = keyBy(Array.isArray(groupResults) ? groupResults : [], 'id')
            setResults(prev => ({
                ...prev,
                ...fromPairs(ids.map(id => [id, resultsById[id] || { error: true }])),
            }))
            computeNext()
        }
        Array.from({ length: PARALLEL_GROUPS }).forEach(() => computeNext())
    }, [searchId, filteredUnitIdsKey])

    const onValidate = useCallback(newFilter => {
        const { conformityFilter: newConformityFilter } = newFilter
        if (!hasValue(newConformityFilter.referenceThreshold) && !hasValue(newConformityFilter.limitThreshold)) {
            dispatch(ToastrAction.error(i18n.noThresholdSelectedError))
            return
        }
        if (!hasValue(newConformityFilter.year)) {
            dispatch(ToastrAction.error(i18n.selectYear))
            return
        }
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE, { searchValue: newFilter.searchValue }))
        dispatch(AdministrationAction.updateSetting(DEFAULT_SUPPORT, newConformityFilter.support || DEFAULT_SUPPORT_VALUE))
        setSearchValue(newFilter.searchValue)
        setFilter(newFilter.filter)
        setFilterResults(newFilter.filterResults)
        setSector(newFilter.sector)
        setCity(newFilter.city)
        setOnlyWithData(newFilter.onlyWithData)
        setConformityFilter(newConformityFilter)
        setCachedFilters({
            searchValue: newFilter.searchValue,
            filter: newFilter.filter,
            filterResults: newFilter.filterResults,
            sector: newFilter.sector,
            city: newFilter.city,
            conformityFilter: newConformityFilter,
        })
        setSearchId(prev => prev + 1)
    }, [dispatch, setOnlyWithData, setCachedFilters])

    // calcul en cours tant qu'une UDI recherchée n'a pas reçu son bilan
    // annulation : les bilans déjà reçus sont conservés, les autres UDI sont marquées comme annulées
    const onCancel = useCallback(() => {
        controllerRef.current.abort()
        setResults(prev => ({
            ...prev,
            ...fromPairs(filteredUnitIds.filter(id => isNil(prev[id])).map(id => [id, { cancelled: true }])),
        }))
    }, [controllerRef, filteredUnitIds])

    const onReset = useCallback(() => {
        controllerRef.current.abort()
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE, { searchValue: '' }))
        setSearchValue('')
        setFilter(FILTER_TYPE.NOFILTER)
        setFilterResults([])
        setSector(undefined)
        setCity(undefined)
        setOnlyWithData(false)
        setConformityFilter(getDefaultConformityFilter())
        removeCachedFilters()
        setResults({})
        setSearchId(0)
        setResetKey(prev => prev + 1)
    }, [controllerRef, dispatch, setOnlyWithData, removeCachedFilters, defaultSupport, thresholdRefDefaultSettings, thresholdRefAccountSettings, thresholdRefSettings, thresholdLimDefaultSettings, thresholdLimAccountSettings, thresholdLimSettings])

    // chargement par défaut : la recherche est lancée une fois, automatiquement, avec les critères par défaut (année en cours),
    // dès que les UDI et les seuils par défaut (réglages chargés de façon asynchrone) sont disponibles
    const [autoSearchDone, setAutoSearchDone] = useState(false)
    useEffect(() => {
        if (autoSearchDone || searchId || !isLoaded || !filteredUnitIds.length) {
            return
        }
        // filtres en cache prioritaires sur les critères par défaut
        const defaultConformityFilter = cachedFilters?.conformityFilter ?? getDefaultConformityFilter()
        if (!hasValue(defaultConformityFilter.referenceThreshold) && !hasValue(defaultConformityFilter.limitThreshold)) {
            return
        }
        setAutoSearchDone(true)
        setConformityFilter(defaultConformityFilter)
        setResetKey(prev => prev + 1) // formulaire remonté avec les seuils par défaut
        setSearchId(1)
    }, [autoSearchDone, searchId, isLoaded, filteredUnitIds, thresholdRefDefaultSettings, thresholdRefAccountSettings, thresholdRefSettings, thresholdLimDefaultSettings, thresholdLimAccountSettings, thresholdLimSettings])

    // avancement : nombre d'UDI recherchées dont le bilan est reçu (ou annulé / en erreur)
    const nbReceived = useMemo(() => filteredUnitIds.filter(id => !isNil(results[id])).length, [filteredUnitIds, results])
    const isComputing = !!searchId && nbReceived < filteredUnitIds.length
    const progressPercent = filteredUnitIds.length ? nbReceived * 100 / filteredUnitIds.length : 0

    // progression extrapolée : avance en continu au rythme moyen observé, sans dépasser les paquets en cours ni reculer
    const [displayedProgress, setDisplayedProgress] = useState(0)
    const searchStartRef = useRef(Date.now())
    const lastReceptionRef = useRef(Date.now())
    useEffect(() => {
        searchStartRef.current = Date.now()
        lastReceptionRef.current = Date.now()
        setDisplayedProgress(0)
    }, [searchId, filteredUnitIdsKey])
    useEffect(() => {
        lastReceptionRef.current = Date.now()
    }, [nbReceived])
    useEffect(() => {
        const total = filteredUnitIds.length
        if (!isComputing || !total) {
            setDisplayedProgress(progressPercent)
            return () => {}
        }
        const inFlight = Math.min(getGroupSize(total) * PARALLEL_GROUPS, total - nbReceived)
        const timer = setInterval(() => {
            const now = Date.now()
            const msPerUnit = nbReceived ? (lastReceptionRef.current - searchStartRef.current) / nbReceived : DEFAULT_MS_PER_UNIT
            const extrapolated = Math.min((now - lastReceptionRef.current) / Math.max(msPerUnit, 1), inFlight * MAX_IN_FLIGHT_RATIO)
            setDisplayedProgress(prev => Math.max(prev, (nbReceived + extrapolated) * 100 / total))
        }, PROGRESS_REFRESH_MS)
        return () => clearInterval(timer)
    }, [isComputing, nbReceived, filteredUnitIds.length, progressPercent])

    // unités sans donnée masquées une fois leur bilan reçu (celles en attente restent affichées avec leur spinner)
    const displayedUnits = useMemo(
        () => onlyWithData && searchId ? filteredUnits.filter(({ id }) => isNil(results[id]) || results[id].error || results[id].cancelled || results[id].nbAnalysis > 0) : filteredUnits,
        [filteredUnits, onlyWithData, results, searchId],
    )

    const onToggleUnit = useCallback((unitId, isExpanded) => {
        setExpandedUnits(prev => ({ ...prev, [unitId]: isExpanded }))
    }, [setExpandedUnits])

    // export global : tous les bilans reçus, une ligne par UDI / producteur / nature
    useActions(() => ({
        export: () => ({
            data: withHeaders(displayedUnits.filter(udi => results[udi.id]?.nbAnalysis)
                .flatMap(udi => getExportData(udi, results[udi.id], conformityFilter, producers))),
            exportType: 'xlsx',
            titleFile: i18n.AEPoverview,
        }),
    }), [displayedUnits, results, conformityFilter, producers])

    if (!isLoaded) {
        return <ProgressCard progress={criteriasProgress} />
    }

    return (
        <Stack spacing={1}>
            <DistributionUnitsConformityFilter
                key={resetKey}
                stationType={STATION_TYPE}
                data={distributionUnitsFormatted}
                defaultSearchValue={searchValue}
                defaultFilter={filter}
                defaultFilterResults={filterResults}
                defaultSector={sector}
                defaultCity={city}
                conformityDefaultFilter={conformityFilter}
                defaultOnlyWithData={onlyWithData}
                criterias={criterias}
                loading={isComputing}
                onReset={onReset}
                onValidate={onValidate}
            />
            <Card>
                <CardTitle title={`${i18n.AEPoverview} (${displayedUnits.length} ${i18n.elements})`} />
                <Grid container sx={{ padding: '1rem' }}>
                    {/* avancement du calcul des bilans, avec annulation : la carte se replie à la fin du calcul */}
                    <Grid size={12}>
                        <Collapse in={isComputing} unmountOnExit>
                            <Box sx={{ marginBottom: '1rem' }}>
                                <ProgressCard
                                    progress={displayedProgress}
                                    withCancel
                                    whenCancel={onCancel}
                                />
                            </Box>
                        </Collapse>
                    </Grid>
                    {displayedUnits.length === 0 ? (
                        <Grid>{i18n.noResults}</Grid>
                    ) : (
                        <Grid size={12}>
                            {displayedUnits.map(udi => (
                                <DistributionUnitConformityAccordion
                                    key={udi.id}
                                    udi={udi}
                                    result={results[udi.id]}
                                    loading={!!searchId && isNil(results[udi.id])}
                                    isSearchDone={!!searchId}
                                    filter={conformityFilter}
                                    expanded={!!expandedUnits[udi.id]}
                                    onToggle={onToggleUnit}
                                />
                            ))}
                        </Grid>
                    )}
                </Grid>
            </Card>
        </Stack>
    )
}

DistributionUnitsConformity.propTypes = {}

export { DistributionUnitConformityAccordion }
export default DistributionUnitsConformity
